import { fetchPortfolioHistoricPerformance } from 'views/Portfolio/components/Summary/services/fetchPortfolioHistoricPerformance';
import { ToolExecutionResult } from '../types';
import { formatCurrency } from '../utils/formatters';

const filterByPeriod = (
  data: Array<{ date: string; currentHoldings: number; netContributions: number }>,
  period?: string,
) => {
  if (!period || period === 'ALL') return data;

  const now = new Date();
  let startDate: Date;

  switch (period) {
    case '1M':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      break;
    case '3M':
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      break;
    case '6M':
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      break;
    case '1Y':
      startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      break;
    default:
      return data;
  }

  return data.filter((point) => new Date(point.date) >= startDate);
};

export const executeGetPortfolioPerformance = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const portfolioId = args.portfolioId as string | undefined;
    const period = args.period as string | undefined;

    const response = await fetchPortfolioHistoricPerformance(portfolioId);

    if (!response.data || response.data.length === 0) {
      return {
        success: true,
        data: {
          message: 'No performance history available',
          performanceData: [],
        },
      };
    }

    const filteredData = filterByPeriod(response.data, period);

    if (filteredData.length === 0) {
      return {
        success: true,
        data: {
          message: `No performance data available for the ${period || 'selected'} period`,
          performanceData: [],
        },
      };
    }

    // Get summary statistics
    const latestPoint = filteredData[filteredData.length - 1];
    const earliestPoint = filteredData[0];

    const valueChange = latestPoint.currentHoldings - earliestPoint.currentHoldings;
    const contributionChange = latestPoint.netContributions - earliestPoint.netContributions;
    const performanceChange = valueChange - contributionChange;

    const percentageChange =
      earliestPoint.currentHoldings > 0
        ? ((latestPoint.currentHoldings - earliestPoint.currentHoldings) / earliestPoint.currentHoldings) * 100
        : 0;

    // Sample the data for summary (take key points)
    const samplePoints = [];
    const step = Math.max(1, Math.floor(filteredData.length / 10));
    for (let i = 0; i < filteredData.length; i += step) {
      samplePoints.push(filteredData[i]);
    }
    // Always include the last point
    if (samplePoints[samplePoints.length - 1] !== latestPoint) {
      samplePoints.push(latestPoint);
    }

    return {
      success: true,
      data: {
        period: period || 'ALL',
        summary: {
          startDate: earliestPoint.date,
          endDate: latestPoint.date,
          startingValue: formatCurrency(earliestPoint.currentHoldings),
          currentValue: formatCurrency(latestPoint.currentHoldings),
          valueChange: formatCurrency(valueChange),
          percentageChange: `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(2)}%`,
          netContributions: formatCurrency(latestPoint.netContributions),
          performanceGain: formatCurrency(performanceChange),
        },
        dataPoints: filteredData.length,
        sampleTimeline: samplePoints.map((point) => ({
          date: point.date,
          holdings: formatCurrency(point.currentHoldings),
          contributions: formatCurrency(point.netContributions),
        })),
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch portfolio performance: ${(error as Error).message}`,
    };
  }
};
